import type React from "react";

/**
 * Make a non-interactive element behave like a button for the keyboard.
 *
 * Spread onto an element whose only affordance was `onClick`:
 *
 *     <div {...clickable(() => open(row))} className="…">
 *
 * It supplies the role, the tab stop, and Enter/Space activation, so the
 * element joins the tab order and can be operated without a pointer. The
 * focus ring comes from `[data-clickable]:focus-visible` in index.css, which
 * avoids having to merge a class into whatever `className` the call site
 * already has.
 *
 * **Only use this when the element has no interactive descendant.** A wrapper
 * that already contains a `<button>` or `<a>` must not also be a button —
 * nested interactive controls are a worse violation than the one this fixes.
 * Those need the action moved onto a real control instead.
 */
export function clickable<T = HTMLElement>(
    onClick: (event: React.MouseEvent<T> | React.KeyboardEvent<T>) => void,
    options?: { label?: string; disabled?: boolean }
) {
    return {
        role: "button" as const,
        tabIndex: options?.disabled ? -1 : 0,
        "data-clickable": "",
        "aria-label": options?.label,
        "aria-disabled": options?.disabled || undefined,
        onClick: (event: React.MouseEvent<T>) => {
            if (options?.disabled) return;
            onClick(event);
        },
        onKeyDown: (event: React.KeyboardEvent<T>) => {
            if (options?.disabled) return;
            if (event.key === "Enter" || event.key === " ") {
                // Space scrolls the page by default; Enter may submit a form.
                event.preventDefault();
                onClick(event);
            }
        },
    };
}
